import React, { useState, useRef, useEffect, useCallback, useId } from "react";
import { CloseIcon } from "./Icons";

function SearchableSelect({
  options = [],
  value,
  onChange,
  placeholder = "Select...",
  searchPlaceholder = "Search...",
  searchable = false,
  paginated = false,
  pageSize = 20,
  disabled = false,
  allowClear = false,
  renderOption,
  getOptionLabel = (opt) => opt.label ?? opt.name ?? String(opt),
  getOptionValue = (opt) => opt.value ?? opt.id ?? opt,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [displayCount, setDisplayCount] = useState(pageSize);
  const [showScrollHint, setShowScrollHint] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(0);
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const listboxId = useId();

  // If not searchable and not paginated, use native select
  const useNativeSelect =
    !searchable && !paginated && !renderOption && !allowClear;

  // Filter options based on search
  const filteredOptions =
    searchable && search.trim()
      ? options.filter((opt) =>
          getOptionLabel(opt).toLowerCase().includes(search.toLowerCase()),
        )
      : options;

  // Paginate
  const displayedOptions = paginated
    ? filteredOptions.slice(0, displayCount)
    : filteredOptions;
  const hasMore = paginated && displayCount < filteredOptions.length;

  // Get selected option object
  const selectedOption = options.find((opt) => getOptionValue(opt) === value);

  // Close dropdown when clicking outside
  useEffect(() => {
    if (useNativeSelect) return;

    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [useNativeSelect]);

  // Reset pagination when search changes
  useEffect(() => {
    setDisplayCount(pageSize);
    setHighlightIndex(0);
  }, [search, pageSize]);

  // Reset highlight when opening
  useEffect(() => {
    if (isOpen) setHighlightIndex(0);
  }, [isOpen]);

  // Keep the highlighted option visible in the scrollable list
  useEffect(() => {
    if (!isOpen || !listRef.current) return;
    const el = listRef.current.children[highlightIndex];
    if (el) el.scrollIntoView({ block: "nearest" });
  }, [highlightIndex, isOpen]);

  // Focus input when opening and check if scrollable
  useEffect(() => {
    if (isOpen && inputRef.current && searchable) {
      inputRef.current.focus();
    }
    // Check if list is scrollable (more content than visible)
    if (isOpen && listRef.current) {
      const { scrollHeight, clientHeight } = listRef.current;
      setShowScrollHint(scrollHeight > clientHeight);
    }
  }, [isOpen, searchable, displayedOptions.length]);

  // Handle scroll for infinite pagination and scroll hint
  const handleScroll = useCallback(() => {
    if (!listRef.current) return;

    const { scrollTop, scrollHeight, clientHeight } = listRef.current;
    const isAtBottom = scrollTop + clientHeight >= scrollHeight - 10;

    // Show scroll hint if not at bottom OR if there are more items to load
    setShowScrollHint(!isAtBottom || hasMore);

    // Load more for paginated lists
    if (hasMore && scrollTop + clientHeight >= scrollHeight - 20) {
      setDisplayCount((prev) => prev + pageSize);
    }
  }, [hasMore, pageSize]);

  const handleSelect = (opt) => {
    onChange(getOptionValue(opt));
    setIsOpen(false);
    setSearch("");
    setDisplayCount(pageSize);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange(null);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Escape") {
      // Consume Escape so it closes the dropdown, not a parent modal
      e.stopPropagation();
      setIsOpen(false);
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightIndex((prev) =>
        Math.min(prev + 1, displayedOptions.length - 1),
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightIndex((prev) => Math.max(prev - 1, 0));
    } else if (e.key === "Enter") {
      // Consume Enter so it selects an option, not a parent modal's submit
      e.preventDefault();
      e.stopPropagation();
      if (displayedOptions[highlightIndex]) {
        handleSelect(displayedOptions[highlightIndex]);
      } else if (displayedOptions.length === 1) {
        handleSelect(displayedOptions[0]);
      }
    }
  };

  // Keyboard support on the trigger (open + navigate when not searchable)
  const handleTriggerKeyDown = (e) => {
    if (disabled) return;
    if (!isOpen) {
      if (e.key === "Enter" || e.key === " " || e.key === "ArrowDown") {
        e.preventDefault();
        e.stopPropagation();
        setIsOpen(true);
      }
      return;
    }
    if (e.key === " " && !searchable) {
      e.preventDefault();
      if (displayedOptions[highlightIndex]) {
        handleSelect(displayedOptions[highlightIndex]);
      }
      return;
    }
    handleKeyDown(e);
  };

  // Native select for simple cases
  if (useNativeSelect) {
    return (
      <select
        value={value ?? ""}
        onChange={(e) => {
          const raw = e.target.value;
          if (raw === "") {
            onChange(null);
            return;
          }
          // Map back to the original option value so native and custom
          // modes emit the same type (e.g. numeric ids stay numeric)
          const match = options.find(
            (opt) => String(getOptionValue(opt)) === raw,
          );
          onChange(match !== undefined ? getOptionValue(match) : raw);
        }}
        disabled={disabled}
      >
        {placeholder && (
          <option value="" disabled>
            {placeholder}
          </option>
        )}
        {options.map((opt, index) => {
          const optValue = getOptionValue(opt);
          return (
            <option key={optValue || index} value={optValue}>
              {getOptionLabel(opt)}
            </option>
          );
        })}
      </select>
    );
  }

  // Custom dropdown for searchable/paginated/custom render
  return (
    <div
      ref={containerRef}
      style={{ position: "relative", marginBottom: "var(--spacing-sm)" }}
    >
      {/* Selected value display / trigger */}
      <div
        onClick={() => !disabled && setIsOpen(!isOpen)}
        onKeyDown={handleTriggerKeyDown}
        tabIndex={disabled ? -1 : 0}
        role="combobox"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-controls={listboxId}
        style={{
          display: "flex",
          alignItems: "center",
          width: "100%",
          height: "44px",
          padding: "0 2.5rem 0 var(--space-4)",
          background: disabled ? "var(--bg-tertiary)" : "var(--bg-primary)",
          border: "1px solid var(--border-color)",
          borderRadius: "var(--radius-md)",
          cursor: disabled ? "not-allowed" : "pointer",
          position: "relative",
        }}
      >
        <span
          style={{
            flex: 1,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            color: selectedOption ? "var(--text-primary)" : "var(--text-muted)",
          }}
        >
          {selectedOption ? getOptionLabel(selectedOption) : placeholder}
        </span>

        {/* Clear button */}
        {allowClear && selectedOption && !disabled && (
          <button
            type="button"
            onClick={handleClear}
            style={{
              position: "absolute",
              right: "2rem",
              background: "none",
              border: "none",
              padding: "4px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              color: "var(--text-muted)",
            }}
          >
            <CloseIcon size={14} />
          </button>
        )}

        {/* Dropdown arrow */}
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{
            position: "absolute",
            right: "0.75rem",
            color: "var(--text-muted)",
            transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.2s",
          }}
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </div>

      {/* Dropdown */}
      {isOpen && (
        <div
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            marginTop: "4px",
            background: "var(--bg-secondary)",
            border: "1px solid var(--border-color)",
            borderRadius: "var(--radius-md)",
            boxShadow: "var(--shadow-lg)",
            zIndex: 100,
            overflow: "hidden",
          }}
        >
          {/* Search input and count */}
          {(searchable ||
            filteredOptions.length > displayedOptions.length ||
            filteredOptions.length > 5) && (
            <div
              style={{
                padding: "8px",
                borderBottom: "1px solid var(--border-color)",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              {searchable ? (
                <input
                  ref={inputRef}
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={searchPlaceholder}
                  style={{
                    flex: 1,
                    height: "36px",
                    padding: "0 12px",
                    background: "var(--bg-primary)",
                    border: "1px solid var(--border-color)",
                    borderRadius: "var(--radius-sm)",
                    fontSize: "0.875rem",
                  }}
                />
              ) : (
                <div style={{ flex: 1 }} />
              )}
              <div
                style={{
                  fontSize: "0.75rem",
                  color: "var(--text-muted)",
                  whiteSpace: "nowrap",
                }}
              >
                {filteredOptions.length}
                {search && filteredOptions.length !== options.length
                  ? ` / ${options.length}`
                  : ""}{" "}
                items
              </div>
            </div>
          )}

          {/* Options list */}
          <div
            ref={listRef}
            id={listboxId}
            role="listbox"
            onScroll={handleScroll}
            className="searchable-select-list"
            style={{
              maxHeight: "200px",
              overflowY: "auto",
              position: "relative",
              scrollbarWidth: "none",
              msOverflowStyle: "none",
            }}
          >
            {displayedOptions.length === 0 ? (
              <div
                style={{
                  padding: "12px 16px",
                  color: "var(--text-muted)",
                  fontSize: "0.875rem",
                  textAlign: "center",
                }}
              >
                {searchable && search ? "No results found" : "No options"}
              </div>
            ) : (
              displayedOptions.map((opt, index) => {
                const optValue = getOptionValue(opt);
                const isSelected = optValue === value;
                const isHighlighted = index === highlightIndex;

                return (
                  <div
                    key={optValue ?? index}
                    onClick={() => handleSelect(opt)}
                    style={{
                      padding: "10px 16px",
                      cursor: "pointer",
                      background: isSelected
                        ? "var(--accent-primary-10, rgba(14, 165, 233, 0.1))"
                        : isHighlighted
                          ? "var(--bg-tertiary)"
                          : "transparent",
                      borderLeft: isSelected
                        ? "3px solid var(--accent-primary)"
                        : "3px solid transparent",
                    }}
                    onMouseEnter={(e) => {
                      setHighlightIndex(index);
                      if (!isSelected) {
                        e.currentTarget.style.background = "var(--bg-tertiary)";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isSelected) {
                        e.currentTarget.style.background = "transparent";
                      }
                    }}
                  >
                    {renderOption ? renderOption(opt) : getOptionLabel(opt)}
                  </div>
                );
              })
            )}

            {/* Floating scroll down button */}
            {(showScrollHint || hasMore) && displayedOptions.length > 0 && (
              <button
                type="button"
                onClick={() => {
                  if (listRef.current) {
                    listRef.current.scrollBy({ top: 100, behavior: "smooth" });
                  }
                }}
                style={{
                  position: "sticky",
                  bottom: "8px",
                  left: "50%",
                  transform: "translateX(-50%)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "32px",
                  height: "32px",
                  margin: "0 auto",
                  background: "var(--accent-primary)",
                  border: "none",
                  borderRadius: "50%",
                  cursor: "pointer",
                  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.3)",
                  transition: "transform 0.15s, background 0.15s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform =
                    "translateX(-50%) scale(1.1)";
                  e.currentTarget.style.background =
                    "var(--accent-primary-hover, var(--accent-primary))";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateX(-50%)";
                  e.currentTarget.style.background = "var(--accent-primary)";
                }}
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="white"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default SearchableSelect;
